/*
 * Copyright (c) 2026 Aegis AO Soft LLC and Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Aegis AO Soft LLC and Alexander Orlov.
 *
 * This code may be used, reproduced, modified, or distributed ONLY with the
 * prior written permission of Aegis AO Soft LLC / Alexander Orlov.
 *
 * Author: Alexander Orlov
 * Aegis AO Soft LLC
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { FlaskConical } from 'lucide-react';
import { isSandbox } from '@/config/sandbox';

/**
 * The always-on "you are in the sandbox" mark.
 *
 * Every screen of the dev deployment carries it, so nobody works for ten minutes before realising
 * the data is not real. It is a label, not a control: no click, no dismiss — the only way it goes
 * away is by being in production, where it renders nothing at all.
 *
 * Three variants for the three places a header can be: the desktop bar, the mobile bar, and the
 * floating pin row that stands in for the desktop bar while it is collapsed.
 */
export const SandboxIndicator: React.FC<{
  variant?: 'header' | 'mobile' | 'pin';
  className?: string;
}> = ({ variant = 'header', className }) => {
  const { t } = useTranslation();

  if (!isSandbox()) return null;

  const label = t('sandbox.badge', 'Sandbox');
  const title = t(
    'sandbox.notice',
    'Test environment. Accounts created here are not real and no payment is ever charged.',
  );

  // The pin variant borrows the chip shape the collapsed header's other actions wear
  // (.pin-floating in index.css) so it lines up with them pixel for pixel.
  if (variant === 'pin') {
    return (
      <span
        role="status"
        title={title}
        className={`pin-floating pin-action pin-action-sandbox ${className ?? ''}`}
      >
        <FlaskConical aria-hidden />
        <span>{label}</span>
      </span>
    );
  }

  const base =
    'inline-flex items-center gap-1 rounded-md bg-amber-400 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-amber-950 whitespace-nowrap';

  return (
    <span
      role="status"
      title={title}
      className={`${base} ${variant === 'mobile' ? 'mr-2' : ''} ${className ?? ''}`}
    >
      <FlaskConical className="h-3 w-3 flex-shrink-0" aria-hidden />
      {label}
    </span>
  );
};

export default SandboxIndicator;
